import LiteTradeSelectAccount
    from "../../../modules/LiteTrade/components/LiteTradeSelectAccount/LiteTradeSelectAccount.jsx";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
    MobileAccountBlockStyled,
    MobileAccountBoxStyled,
    MobileAccountStatButtonStyled,
    MobileAccountTopBoxStyled
} from "./styled/MobileAccountBoxStyled.js";
import { Button, FormGroup } from "react-bootstrap";
import amountFormat from "../../../utils/amountFormat.js";
import { createStructuredSelector } from "reselect";
import {
    getAccountStatPL,
    makeSelectorGetLiteTradeAccountMetrics
} from "../../../modules/LiteTrade/liteTradeSelectors.js";
import { connect } from "react-redux";
import classNames from "classnames";
import { BinIcon } from "../../../components/icon/Icon.jsx";
import { openMobileSliderPageAction } from "../../mobileAppActions.js";
import { MOBILE_SLIDER_PAGES_IDS } from "../MobileSliderPages/mobileSliderPagesConstants.js";
import { useScrollProviderContext } from "../../../providers/ScrollProvider/ScrollProviderContext.js";
import { ACCOUNT_TYPES, LOG_AF_EVENT_NAMES } from "../../../app/appConstants.js";
import parseAccountName from "../../../utils/parseAccountName.js";
import { logAppsflyerAction, setUserSelectedAccountCode } from "../../../app/appActions.js";
import { getUserHasLiveAccount } from "../../../app/appSelectors.js";
import { Slider } from "../../../components/Slider/Slider.jsx";
import withDebounced from "../../../hooks/withDebounced.js";
import { useLangContext } from "../../../providers/LangProvider/LangProviderContext";
import { getLiteTradeAccountStatPLAction } from "../../../modules/LiteTrade/liteTradeActions.js";
import { PRESET_STAT_PL } from "../../../modules/LiteTrade/liteTradeConstants.js";
import { getIsRespawnAvailable } from "../../../modules/Bonus/bonusesSelectors";
import MobileAccountBoxTopUpButton from "./MobileAccountBoxTopUpButton";

function MobileAccountBox(props) {
    const {
        accountCode,
        hasLiveAccount,
        isRespawnAvailable,
        logAppsflyer,
        metrics,
        openMobileSliderPage,
        setSelectedAccountCode,
        accountStatPL,
        getLiteTradeAccountStatPL,
    } = props;

    const { scrollPosition } = useScrollProviderContext();
    const accountType = useMemo(() => parseAccountName(accountCode)?.type, [accountCode]);
    const { getLangPath } = useLangContext();
    const [statPLPreset, setStatPLPreset] = useState(PRESET_STAT_PL.DAY);

    useEffect(() => {
        if (accountCode) {
            getLiteTradeAccountStatPL(statPLPreset);
            const timer = setInterval(() => {
                getLiteTradeAccountStatPL(statPLPreset);
            }, 10000);
            return () => {
                clearTimeout(timer);
            }
        }
    }, [accountCode, getLiteTradeAccountStatPL, statPLPreset]);

    const handleDepositButtonClick = useCallback((e) => {
        e.preventDefault();
        openMobileSliderPage(MOBILE_SLIDER_PAGES_IDS.DEPOSIT);
        logAppsflyer({
            name: LOG_AF_EVENT_NAMES.MAIN_DEPOSIT,
            payload: { account_type: accountType, source: 'portfolio' },
        });
    }, [accountType, logAppsflyer, openMobileSliderPage]);

    const handleWithdrawButtonClick = useCallback((e) => {
        e.preventDefault();
        openMobileSliderPage({ name: MOBILE_SLIDER_PAGES_IDS.WITHDRAW });
        logAppsflyer({
            name: LOG_AF_EVENT_NAMES.MAIN_WITHDRAW,
            payload: { account_type: accountType, source: 'portfolio' },
        });
    }, [openMobileSliderPage, logAppsflyer, accountType]);

    const handleStartRealButtonClick = useCallback((e) => {
        e.preventDefault();
        const accountCodeLive = parseAccountName(accountCode).getAccountCode(ACCOUNT_TYPES.LIVE);
        setSelectedAccountCode(accountCodeLive);
    }, [accountCode, setSelectedAccountCode]);

    const handleRespawnButtonClick = useCallback((e) => {
        e.preventDefault();
        openMobileSliderPage(MOBILE_SLIDER_PAGES_IDS.RESPAWN);
        logAppsflyer({
            name: LOG_AF_EVENT_NAMES.MAIN_RESPAWN,
            payload: { account_type: accountType },
        });
    }, [accountType, logAppsflyer, openMobileSliderPage]);

    const handleTransactionsButtonClick = useCallback((e) => {
        e.preventDefault();
        openMobileSliderPage(MOBILE_SLIDER_PAGES_IDS.HISTORY);
        logAppsflyer({
            name: LOG_AF_EVENT_NAMES.MAIN_TRANSACTIONS,
            payload: { account_type: accountType },
        });
    }, [accountType, logAppsflyer, openMobileSliderPage]);

    const handleAccountStat = useCallback((e) => {
        e.preventDefault();
        openMobileSliderPage(MOBILE_SLIDER_PAGES_IDS.ACCOUNT_STAT);
        logAppsflyer({
            name: LOG_AF_EVENT_NAMES.MAIN_ACCOUNT_ANALYTICS,
            payload: { account_type: accountType },
        });
    }, [accountType, logAppsflyer, openMobileSliderPage]);

    const isLive = useMemo(() => {
        return parseAccountName(accountCode)?.type === ACCOUNT_TYPES.LIVE;
    }, [accountCode]);

    const getBlockPl = useMemo(() => {
        if (accountStatPL && metrics) {
            const balance = metrics.balance + metrics.credit;
            const pl = parseFloat(accountStatPL.pl) + metrics.openPL;
            const diffPl = (pl / balance * 100) || 0;
            let precision = 0;
            if (diffPl < 10) precision = 2;
            return <span className={'sum-pl'}>
                <span className={classNames({
                    'positive': pl > 0,
                    'negative': pl < 0,
                })}>{amountFormat(pl || 0, 2, '$')}
                    {
                        diffPl !== 0 && <>
                            <span className={'pl-diff'}>
                                <BinIcon style={{ fontSize: '1.25em' }}
                                         name={diffPl > 0 ? 'IcGrowth' : 'IcReduction'}/> {amountFormat(Math.abs(diffPl), precision)}%
                            </span>
                            <span className={'pl-diff-period'}>
                                {getLangPath(`lite_version.home.presets_stat_pl.${accountStatPL.preset}`)}
                            </span>
                        </>
                    }</span>
            </span>

        }
    }, [accountStatPL, getLangPath, metrics]);

    if (!metrics) {
        return null;
    }

    return <MobileAccountBlockStyled className={classNames({
        'scrolled': scrollPosition > 150
    })}>
        <MobileAccountTopBoxStyled className={'top-box'}>
            <div className="equity mb-1">
                <span className="text-secondary fw-normal">$</span>{amountFormat(metrics.equity || 0)}
            </div>
            {getBlockPl}
        </MobileAccountTopBoxStyled>

        <FormGroup className={'mb-3 d-flex justify-content-between'}>
            <div>
                <LiteTradeSelectAccount/>
            </div>
            <div className={'f24'}>
                <Slider showCloseButton={false}
                        placement={'bottom'}
                        toggleComponent={<BinIcon name={'IcGearApp'}/>}
                >
                    {
                        hasLiveAccount && <>
                            {
                                isLive ? <>
                                    <Slider.Row className={'dropdown-item'} onClick={handleWithdrawButtonClick}>
                                        <BinIcon name={'IcWithdraw'}
                                                 style={{ fill: 'rgba(255, 255, 255, 0.6)' }}/><span>{getLangPath('lite_version.home.withdraw')}</span>
                                    </Slider.Row>
                                    <Slider.Row className={'dropdown-item'} onClick={handleDepositButtonClick}>
                                        <BinIcon name={'IcPlusCircle'}
                                                 style={{ fill: 'rgba(255, 255, 255, 0.6)' }}/><span>{getLangPath('lite_version.home.top_up')}</span>
                                    </Slider.Row>
                                </> : <>
                                    <Slider.Row className={'dropdown-item'} onClick={handleStartRealButtonClick}>
                                        <BinIcon name={'IcPlusCircle'}
                                                 style={{ fill: 'rgba(255, 255, 255, 0.6)' }}/><span>{getLangPath('lite_version.home.start_real')}</span>
                                    </Slider.Row>
                                </>
                            }
                        </>
                    }
                    <Slider.Row className={'dropdown-item'} onClick={handleTransactionsButtonClick}>
                        <BinIcon name={'IcMenuBurger'}
                                 style={{ stroke: 'rgba(255, 255, 255, 0.6)' }}/><span>{getLangPath('lite_version.home.history')}</span>
                    </Slider.Row>
                    {isRespawnAvailable && (
                        <Slider.Row className={'dropdown-item'} onClick={handleRespawnButtonClick}>
                            <BinIcon name={'IcRebuy'}
                                     style={{ fill: 'rgba(255, 255, 255, 0.6)' }}/><span>{getLangPath('lite_version.home.respawn')}</span>
                        </Slider.Row>
                    )}
                </Slider>
            </div>
        </FormGroup>

        <MobileAccountBoxStyled>
            <MobileAccountStatButtonStyled>
                <Button variant={'info'} onClick={handleAccountStat}>
                    <BinIcon name={'IcChartLine'}/>
                </Button>
            </MobileAccountStatButtonStyled>
            <h1><span className="text-secondary">$</span>{amountFormat(metrics.equity || 0)}</h1>
            {getBlockPl}
            <div className={'buttons'}>
                {
                    hasLiveAccount && <>
                        {
                            (parseAccountName(accountCode).type === ACCOUNT_TYPES.LIVE) ? <>
                                <Button variant={'info'} onClick={handleWithdrawButtonClick} name={'withdraw'}>
                                    <BinIcon name={'IcWithdraw'}/>
                                    <div>{getLangPath('lite_version.home.withdraw')}</div>
                                </Button>
                                <MobileAccountBoxTopUpButton onClick={handleDepositButtonClick}/>
                            </> : <>
                                <Button variant={'info'} onClick={handleStartRealButtonClick} name={'start_real'}>
                                    <BinIcon name={'IcPlusCircle'}/>
                                    <div>{getLangPath('lite_version.home.start_real')}</div>
                                </Button>
                            </>
                        }
                    </>
                }
                <Button variant={'info'} onClick={handleTransactionsButtonClick}>
                    <BinIcon name={'IcMenuBurger'}/>
                    <div>{getLangPath('lite_version.home.history')}</div>
                </Button>
            </div>
        </MobileAccountBoxStyled>
    </MobileAccountBlockStyled>
}

const mapStateToProps = createStructuredSelector({
    accountStatPL: getAccountStatPL,
    hasLiveAccount: getUserHasLiveAccount,
    isRespawnAvailable: getIsRespawnAvailable,
    metrics: (state, props) => makeSelectorGetLiteTradeAccountMetrics(props.accountCode)(state, props),
})

const mapDispatchToProps = (dispatch) => ({
    logAppsflyer: payload => dispatch(logAppsflyerAction(payload)),
    openMobileSliderPage: payload => dispatch(openMobileSliderPageAction(payload)),
    setSelectedAccountCode: payload => dispatch(setUserSelectedAccountCode(payload)),
    getLiteTradeAccountStatPL: payload => dispatch(getLiteTradeAccountStatPLAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withDebounced({
    propNames: ['metrics'],
    debounce: 1000
})(memo(MobileAccountBox)));
